import { useState } from 'react';
import {
  api, HOW_LABEL, type QuestionHow, type Classes as ClassesData, type ClassRow, type MeasureCounts, type SettableMode, type SpendDay, type ExperimentResult,
} from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { usd, dayLocal, whenLocal } from '../lib/time';
import './classes.css';

/**
 * What each class of card may do on its own, and what it costs.
 *
 * PROMOTION IS YOURS (Phase 8.4). A class moves when you move it here, with a
 * confirm, and the desk records who, before and after. Nothing on this screen
 * or behind it promotes a class by itself. Demotion is automatic: a class at
 * veto whose stops outrun its limit drops to approve and says so in Slack.
 *
 * The outcomes panel shows CUSTOMER outcomes — converted, worked, churned —
 * not card statuses. `closed_by_us` is its own line and is never drawn as
 * churn: an account our own ladder closed is our policy working.
 */
export function Classes({ onConfirm }: { onConfirm: (r: ConfirmRequest) => void }) {
  const r = useResource<ClassesData>(() => api.classes(), { pollMs: 60_000 });
  const [error, setError] = useState<string | null>(null);

  if (r.loading && !r.data) return <main className="board"><p className="list-empty">Reading the classes…</p></main>;
  if (!r.data) return <main className="board"><p className="list-empty list-error">{r.error?.message}</p></main>;

  const d = r.data;
  const change = (c: ClassRow, mode: SettableMode, hours: number | null) => onConfirm({
    title: `Move ${c.label} to ${mode}${mode === 'veto' ? ` ${hours}h` : ''}?`,
    detail: consequence(c, mode, hours),
    confirmLabel: `Set ${mode}`,
    tone: mode === 'veto' ? 'danger' : 'normal',
    askReason: true,
    reasonLabel: 'Why (optional, recorded with the change)',
    onConfirm: async (reason) => {
      setError(null);
      try {
        await api.setMode(c.class, mode, hours, reason || undefined);
      } catch (e) {
        setError((e as Error).message);
      }
      r.refetch();
    },
  });

  return (
    <main className="board">
      <div className="classes-head">
        <div>
          <div className="mono eyebrow">Classes &amp; spend</div>
          <h1>What each class may do on its own, and what it costs</h1>
        </div>
        <div className="global-stop">
          <span className="mono global-stop-label">Global stop</span>
          <span className={`global-stop-state ${d.halt.on ? 'is-halted' : ''}`}>
            {d.halt.on ? 'Outbound HALTED' : 'Outbound on'}
          </span>
          <span className="mono global-stop-when">
            {d.halt.last_changed_at
              ? `· last changed ${dayLocal(d.halt.last_changed_at)}${d.halt.last_changed_by ? ` by ${d.halt.last_changed_by}` : ''}`
              : '· never changed'}
          </span>
        </div>
      </div>

      <div className="classes-grid">
        <section className="panel classes">
          <div className="panel-head">
            <h2>Classes</h2>
            <span className="mono panel-note">last 30 days · automatic sends only 08:00–21:00 Brisbane</span>
          </div>
          {error && <p className="list-error classes-error">{error}</p>}

          <div className="class-rows">
            {d.classes.map((c) => <ClassLine key={c.class} c={c} onChange={change} />)}
          </div>
        </section>

        <div className="classes-right">
          <section className="panel">
            <div className="panel-head">
              <h2>Model spend · 7 days</h2>
              <span className="spend-total">{usd(d.spend.total_usd)}</span>
            </div>
            <SpendChart days={d.spend.days} />
            <div className="spend-legend mono">
              {d.spend.by_model.length === 0
                ? <span>No model calls in the last seven days.</span>
                : d.spend.by_model.map((m, i) => (
                  <span key={m.model} className="spend-key">
                    <span className="swatch" style={{ background: seriesColour(i) }} />
                    {m.model} · {usd(m.usd)}
                  </span>
                ))}
            </div>
          </section>

          <DrafterPanel m={d.drafter} />

          <section className="panel panel-grow">
            <div className="panel-head">
              <h2>Outcomes · {d.outcomes_rates_unlocked ? 'over 30 days' : 'counts, not rates'}</h2>
              <span className="mono panel-note">{d.outcomes_note}</span>
            </div>

            {d.outcomes.length === 0 ? (
              // An empty panel with sends in flight has to say WHY it is
              // empty, or a system that is working perfectly and has simply
              // not waited four days yet reads as a system measuring nothing.
              <p className="list-empty">
                {d.outcomes_waiting > 0
                  ? `Nothing has come of age yet — ${d.outcomes_waiting} send${d.outcomes_waiting === 1 ? '' : 's'} waiting.`
                  : 'No sends to judge yet.'}
              </p>
            ) : (
              <div className="outcomes">
                {d.outcomes.map((o) => (
                  <div key={o.outcome} className={`outcome outcome-${o.outcome}`}>
                    <span className={o.tone === 'good' ? 'is-teal' : o.tone === 'bad' ? 'is-rust' : ''}>{o.label}</span>
                    <span className="mono">{o.count}</span>
                  </div>
                ))}
              </div>
            )}

            {d.outcomes.length > 0 && d.outcomes_waiting > 0 && (
              <p className="mono outcomes-waiting">
                {d.outcomes_waiting} more sent and not yet old enough to judge
              </p>
            )}
            <p className="panel-prose outcomes-source">{d.outcomes_source}</p>
          </section>

          {d.experiments.length > 0 && (
            <section className="panel">
              <div className="panel-head">
                <h2>Holdout</h2>
                <span className="mono panel-note">
                  {d.experiments.some((e) => e.status === 'running')
                    ? 'half are not sent it, by a stable hash' : 'none running'}
                </span>
              </div>
              {d.experiments.map((e) => <Experiment key={e.experiment_id} e={e} />)}
            </section>
          )}
        </div>
      </div>
    </main>
  );
}

/**
 * One holdout experiment. An ended one says so first, and its verdict is the
 * result recorded when it was stopped — never judged here (fix-87, #233).
 *
 * It prints the arms and a SENTENCE, never a rate, until there are enough
 * accounts for a difference to mean something. v1 reported "0 of 21 converted
 * never made a call" as though 21 were a sample and it was quoted for weeks.
 */
function Experiment({ e }: { e: ExperimentResult }) {
  const ended = e.status !== 'running';
  return (
    <div className={`experiment ${ended ? 'is-ended' : ''}`}>
      <p className="experiment-status mono">
        {ended ? `Ended ${dayLocal(e.stopped_at)}` : `Running since ${dayLocal(e.started_at)}`}
      </p>
      <p className="experiment-q">{e.question}</p>
      <div className="experiment-arms mono">
        <span>treatment {e.treatment.n} · {e.treatment.paid} paid · {e.treatment.called} called</span>
        <span>holdout {e.holdout.n} · {e.holdout.paid} paid · {e.holdout.called} called</span>
      </div>
      <p className={`experiment-verdict ${e.conclusive ? 'is-conclusive' : ''}`}>{e.verdict}</p>
    </div>
  );
}

/** What a change will do, said before it is made. */
// fix-62 (#193): an answer typed on a card is not a correction, so it is shown beside the measure and never in it.
// fix-57: counted twice — since the model's change and since the Drafter instructions were last saved.
function DrafterPanel({ m }: { m: ClassesData['drafter'] | undefined }) {
  if (!m) return null;
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Reply drafts</h2>
        <span className="mono panel-note">Refines + Rejects per 20 drafts · answers not counted · share changed per sent draft (0 untouched, 100 replaced)</span>
      </div>
      <div className="drafter-rows">
        <MeasureRow label="Since the model change" c={m} />
        {m.instructions && <MeasureRow label="Since the last instructions change" c={m.instructions} />}
        {m.last_20 && (
          <div className="drafter-measure">
            <div className="mono panel-note">The last 20 sent drafts</div>
            <div className="drafter-per20">
              <span className="drafter-per20-n">{shareText(m.last_20.share_changed)}</span>
              <span className="drafter-counts mono">share changed · {m.last_20.sent} sent</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function MeasureRow({ label, c }: { label: string; c: MeasureCounts }) {
  return (
    <div className="drafter-measure">
      <div className="mono panel-note">{label}{c.error ? '' : ` · ${whenLocal(c.from)}`}</div>
      {c.error ? <p className="list-error">{c.error}</p> : (
        <>
          <div className="drafter-per20">
            <span className="drafter-per20-n">{c.per_20 === null ? '—' : c.per_20.toFixed(1)}</span>
            <span className="drafter-counts mono">
              {c.drafts} drafts · {c.refines} Refines · {c.rejects} Rejects · {c.answers} Answers
            </span>
          </div>
          {/* answer-1: the questions drafts answered from knowledge, by inference, partly (partly-1) and left to you, per 20 drafts. */}
          {c.questions && (
            <div className="drafter-per20">
              <span className="drafter-counts mono">
                per 20 drafts: {(Object.keys(HOW_LABEL) as QuestionHow[]).map((h) => `${c.questions![h] ?? '—'} ${HOW_LABEL[h]}`)
                  .join(' · ')} · {c.questions.drafts} drafts list their questions
              </span>
            </div>
          )}
          {/* fix-73: how much of the AI's first draft was changed before it went — the drafter's measure, Refines included. */}
          {c.sent !== undefined && (
            <div className="drafter-per20">
              <span className="drafter-per20-n">{shareText(c.share_changed ?? null)}</span>
              <span className="drafter-counts mono">share changed per draft · {c.sent} sent</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const shareText = (n: number | null) => (n === null ? '—' : `${n}%`);

function consequence(c: ClassRow, mode: SettableMode, hours: number | null): string {
  const waiting = 'Cards already waiting are not given a window.';
  if (mode === 'veto') {
    return `New cards in ${c.label} will send on their own ${hours} hour${hours === 1 ? '' : 's'} `
      + 'after they are raised unless you stop them. Automatic sends only go between 08:00 and 21:00 '
      + `Brisbane; a deadline outside that waits for 08:00. ${waiting} If stops pass `
      + `${Math.round(c.stop_limit_30d * 100)}% of its sends, it drops back to approve by itself.`;
  }
  const leaving = c.mode === 'veto' ? ' Cards in a veto window lose it and wait for you.' : '';
  return mode === 'hold'
    ? `Every card in ${c.label} will be held for you.${leaving}`
    : `Every card in ${c.label} will wait for you to approve it.${leaving}`;
}

function ClassLine({ c, onChange }: {
  c: ClassRow;
  onChange: (c: ClassRow, mode: SettableMode, hours: number | null) => void;
}) {
  const [mode, setMode] = useState<SettableMode>(c.mode === 'auto' ? 'hold' : c.mode);
  const [hours, setHours] = useState<string>(String(c.veto_hours ?? 2));
  const h = Number(hours);
  const hoursOk = mode !== 'veto' || (Number.isFinite(h) && h > 0 && h <= 48);
  const same = mode === c.mode && (mode !== 'veto' || h === c.veto_hours);

  return (
    <div className="class-row">
      <div>
        <div className="class-name">{c.label}</div>
        <div className="mono class-detail">
          {c.detail}
          {c.mode_changed_at ? ` · set ${dayLocal(c.mode_changed_at)}${c.mode_changed_by ? ` by ${c.mode_changed_by}` : ''}` : ''}
        </div>
      </div>

      <span className={`mono mode mode-${c.mode}`}>
        {c.mode}{c.mode === 'veto' && c.veto_hours ? ` ${c.veto_hours}h` : ''}
      </span>

      <span className="mono class-counts">
        {c.sent} sent · {c.stopped} stopped · {c.dismissed} dismissed · {c.rejected} rejected
        {c.class === 'consequential' ? ` · ${c.overridden ?? 0} overridden` : ''}
      </span>

      {c.fixed ? (
        <span className="mono class-fixed">fixed by policy</span>
      ) : (
        <div className="class-change">
          <select
            aria-label={`Mode for ${c.label}`}
            value={mode}
            onChange={(e) => setMode(e.target.value as SettableMode)}
          >
            <option value="approve">approve</option>
            <option value="veto">veto</option>
            <option value="hold">hold</option>
          </select>
          {mode === 'veto' && (
            <input
              aria-label={`Veto hours for ${c.label}`}
              className="class-hours mono"
              type="number" min={1} max={48} step={1}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          )}
          <button
            type="button"
            className="btn-ghost"
            disabled={same || !hoursOk}
            onClick={() => onChange(c, mode, mode === 'veto' ? h : null)}
          >
            Set
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Seven bars, stacked by model.
 *
 * Every day is drawn, including the ones nothing was spent on — a chart that
 * skips an empty day draws six bars, labels them seven, and turns a quiet
 * Sunday into a busy one.
 */
function SpendChart({ days }: { days: SpendDay[] }) {
  const peak = Math.max(...days.map((d) => d.total), 0.0001);
  const models = [...new Set(days.flatMap((d) => Object.keys(d.by_model)))];

  return (
    <>
      <div className="spend-chart" role="img" aria-label={`Model spend over ${days.length} days, peak ${usd(peak)}`}>
        {days.map((d) => (
          <div key={d.day} className="spend-col" title={`${d.day}: ${usd(d.total)}`}>
            {d.total === 0
              ? <span className="spend-zero" />
              : models.map((m, i) => (
                d.by_model[m]
                  ? <span key={m} style={{ height: `${(d.by_model[m] / peak) * 100}%`, background: seriesColour(i) }} />
                  : null
              ))}
          </div>
        ))}
      </div>
      <div className="spend-days mono">
        {days.map((d) => <span key={d.day}>{weekday(d.day)}</span>)}
      </div>
    </>
  );
}

/**
 * The spend series palette: the accent, then progressively paler versions of
 * it. The chart compares one quantity split by model, so the series belong to
 * one hue — a rainbow would imply they are different kinds of thing.
 */
function seriesColour(i: number): string {
  return ['var(--teal)', 'var(--teal-pale)', 'var(--teal-deep)', 'var(--amber)'][i % 4];
}

function weekday(day: string): string {
  const t = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(t)
    ? new Intl.DateTimeFormat('en-AU', { weekday: 'short', timeZone: 'UTC' }).format(new Date(t))
    : day;
}
